<template>
    <div class="cat-node">
        <div
            class="cat-row"
            :class="{ 'cat-row--branch': hasChildren, 'cat-row--open': isOpen }"
            @click="onRowClick"
        >
            <span
                v-if="hasChildren"
                class="cat-caret"
                :class="{ 'cat-caret--open': isOpen }"
                @click.stop="toggle"
            >
                <i class="bi bi-chevron-right"></i>
            </span>
            <span v-else class="cat-caret cat-caret--empty"></span>

            <span class="cat-icon">
                <i class="bi" :class="hasChildren ? (isOpen ? 'bi-folder2-open' : 'bi-folder2') : 'bi-tag'"></i>
            </span>

            <span class="cat-name">
                <template v-for="(seg, si) in nameParts" :key="si">
                    <mark v-if="seg.hit" class="cat-hl">{{ seg.text }}</mark>
                    <template v-else>{{ seg.text }}</template>
                </template>
            </span>

            <span v-if="isRoot" class="cat-tag cat-tag--root">{{ t('pages.stock.category.rootBadge') }}</span>

            <span v-for="m in moduleList" :key="m.id" class="cat-tag cat-tag--module">{{ m.name }}</span>

            <span v-if="hasChildren" class="cat-count">{{ subCountText }}</span>

            <span class="cat-actions" @click.stop>
                <el-tooltip :content="t('pages.stock.category.addSub')" placement="top">
                    <button type="button" class="cat-act" @click="api.onAddChild(node)">
                        <i class="bi bi-plus-lg"></i>
                    </button>
                </el-tooltip>
                <el-tooltip :content="t('common.edit')" placement="top">
                    <button type="button" class="cat-act" @click="api.onEdit(node)">
                        <i class="bi bi-pencil"></i>
                    </button>
                </el-tooltip>
                <el-tooltip :content="t('common.delete')" placement="top">
                    <button type="button" class="cat-act cat-act--danger" @click="api.onDelete(node)">
                        <i class="bi bi-trash"></i>
                    </button>
                </el-tooltip>
            </span>
        </div>

        <div v-if="hasChildren && isOpen" class="cat-children">
            <CategoryTreeNode
                v-for="child in visibleChildren"
                :key="child.id"
                :node="child"
                :depth="depth + 1"
            />
        </div>
    </div>
</template>

<script>
import CategoryTreeNode from "@/pages/stock/category/CategoryTreeNode.vue";

export default {
    name: "CategoryTreeNode",
    components: { CategoryTreeNode },
    inject: ["catApi"],
    props: {
        node: { type: Object, required: true },
        depth: { type: Number, default: 0 }
    },
    computed: {
        api() {
            return this.catApi;
        },
        isRoot() {
            return !this.node.parent_id;
        },
        children() {
            return Array.isArray(this.node.children) ? this.node.children : [];
        },
        hasChildren() {
            return this.children.length > 0;
        },
        visibleChildren() {
            return this.api.filterChildren(this.children);
        },
        isOpen() {
            return this.api.isExpanded(this.node.id);
        },
        moduleList() {
            return Array.isArray(this.node.modules) ? this.node.modules : [];
        },
        subCountText() {
            return this.sprintf(this.$t("pages.stock.category.subCountPattern"), [this.descendantCount]);
        },
        descendantCount() {
            return this.api.descendantCount(this.node);
        },
        nameParts() {
            const term = (this.api.searchTerm || "").trim();
            const name = this.node.name ?? "";
            if (!term) {
                return [{ text: name, hit: false }];
            }
            const lower = name.toLocaleLowerCase("tr-TR");
            const needle = term.toLocaleLowerCase("tr-TR");
            const parts = [];
            let from = 0;
            let idx = lower.indexOf(needle, from);
            while (idx !== -1) {
                if (idx > from) {
                    parts.push({ text: name.slice(from, idx), hit: false });
                }
                parts.push({ text: name.slice(idx, idx + needle.length), hit: true });
                from = idx + needle.length;
                idx = lower.indexOf(needle, from);
            }
            if (from < name.length) {
                parts.push({ text: name.slice(from), hit: false });
            }
            return parts.length ? parts : [{ text: name, hit: false }];
        }
    },
    methods: {
        t(key) {
            return this.$t(key);
        },
        toggle() {
            this.api.toggle(this.node.id);
        },
        onRowClick() {
            if (this.hasChildren) {
                this.toggle();
            }
        }
    }
};
</script>

<style scoped>
.cat-node {
    position: relative;
}

.cat-children {
    margin-left: 20px;
    padding-left: 11px;
    border-left: 1px solid var(--cat-guide);
}

.cat-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 8px;
    cursor: default;
    position: relative;
    min-height: 38px;
    transition: background-color 0.12s ease;
}

.cat-row--branch {
    cursor: pointer;
}

.cat-row:hover {
    background-color: var(--cat-row-hover);
}

.cat-caret {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    color: var(--cat-muted);
    font-size: 11px;
    transition: transform 0.15s ease, background-color 0.12s ease;
}

.cat-caret:not(.cat-caret--empty):hover {
    background-color: var(--cat-caret-hover);
    color: var(--cat-fg);
}

.cat-caret--open {
    transform: rotate(90deg);
}

.cat-caret--empty {
    cursor: default;
}

.cat-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    color: var(--cat-accent);
    font-size: 15px;
}

.cat-caret i,
.cat-icon i,
.cat-act i {
    display: block;
    line-height: 1;
}

.cat-row--branch:not(.cat-row--open) .cat-icon {
    color: var(--cat-accent);
}

.cat-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--cat-fg);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cat-hl {
    background-color: var(--cat-hl-bg);
    color: var(--cat-hl-fg);
    padding: 0 1px;
    border-radius: 3px;
}

.cat-tag {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    height: 19px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.2px;
}

.cat-tag--root {
    background-color: var(--cat-rootbadge-bg);
    color: var(--cat-rootbadge-fg);
    text-transform: uppercase;
}

.cat-tag--module {
    background-color: var(--cat-chip-bg);
    color: var(--cat-chip-fg);
}

.cat-count {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    color: var(--cat-muted);
    font-variant-numeric: tabular-nums;
}

.cat-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;
    opacity: 0;
    transform: translateX(4px);
    transition: opacity 0.12s ease, transform 0.12s ease;
}

.cat-row:hover .cat-actions,
.cat-actions:focus-within {
    opacity: 1;
    transform: translateX(0);
}

.cat-act {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 6px;
    background-color: transparent;
    color: var(--cat-muted);
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.cat-act:hover {
    background-color: var(--cat-act-hover);
    color: var(--cat-accent);
}

.cat-act--danger:hover {
    background-color: var(--cat-danger-bg);
    color: var(--cat-danger-fg);
}
</style>
